/* eslint-disable max-lines, complexity */
import React from 'react'
import { func, number, object, shape, string } from 'prop-types'
import PAction from '../PAction/PAction'
import IcoCard from '../icons/IcoCard'
import IcoPin from '../icons/IcoPin'
import IcoStop from '../icons/IcoStop'
import IcoCheck from '../icons/IcoCheck'
import IcoHeart from '../icons/IcoHeart'
import IcoCalendar from '../icons/IcoCalendar'
import Brindes from './Brindes'
import { formatBRL } from '../../data/pricing'
import { derivarEdicaoRenovacao, derivarValorPagoEdicaoAtual } from '../../data/assinatura-utils'
import { brindesDisponiveis } from '../../data/brindes'
import {
  Root,
  Head,
  HeadBrand,
  Title,
  RenewLine,
  StatusPill,
  StatusDot,
  StatusPillCal,
  HeroStats,
  HeroPrice,
  StatKey,
  PriceValue,
  PriceOld,
  PriceBig,
  PriceSmall,
  PriceNotes,
  PriceNote,
  Actions,
  ActionsEyebrow,
  ActionsNota,
  ActionsGrid,
  AlertaFalha,
  AlertaFalhaIcon,
  AlertaFalhaBody,
  AlertaFalhaBtn
} from './styles'
import {
  STATUS_CONFIG,
  parseDate,
  formatarRenovacaoFull
} from './passport-utils'

export default function Passport( {
  assinatura,
  associado,
  nomeAssinatura,
  cardPrincipal,
  addrPrincipal,
  totalAddrs,
  formaPagamento,
  precoRenovacao,
  onRenewClick,
  onPaymentClick,
  onAddressClick,
  onCancelClick,
  onReactivateClick,
  onUndoClick
} ) {
  const [ primeiroNome ] = ( associado?.nome || 'Associado' ).split( ' ' )
  const st = STATUS_CONFIG[ assinatura.status ]

  const podeRenovar = assinatura.podeRenovar !== false
  const dataRenovacao = podeRenovar
    ? parseDate( assinatura.dataProximaRenovacaoAutomatica )
    : null

  const isCancelamentoAgendado = st.variant === 'solicitouCancelamento'
  const renewFull = formatarRenovacaoFull( dataRenovacao )

  const cartaoMeta = ( () => {
    if ( formaPagamento === 'pix' ) {
      return 'Pix'
    }
    if ( cardPrincipal ) {
      return `${cardPrincipal.brand} •••• ${cardPrincipal.last4}`
    }
    return 'Nenhum cartão salvo'
  } )()

  const enderecoMeta = ( () => {
    if ( !addrPrincipal ) {
      return 'Nenhum endereço salvo'
    }
    const extras = totalAddrs > 1 ? ` +${totalAddrs - 1}` : ''
    return `${addrPrincipal.cidade}/${addrPrincipal.uf}${extras}`
  } )()

  const temDescontoAssociado = Boolean( precoRenovacao ) && precoRenovacao.promo < precoRenovacao.original

  const isCancelada = st.variant === 'cancelada'
  const isCancelamentoEmCurso = isCancelamentoAgendado || isCancelada
  const isAtiva = !isCancelada && !isCancelamentoAgendado

  const edicaoBookster = assinatura.edicaoBookster || null
  const edicaoRenovacao = derivarEdicaoRenovacao( assinatura )
  const valorPago = isAtiva ? derivarValorPagoEdicaoAtual( assinatura ) : null
  const reativouEdicaoAtual = assinatura.edicaoAtualPorReativacao === true
  const brindes = brindesDisponiveis( { 'contextoReativacao': isCancelada || reativouEdicaoAtual } )
  const termoRenovacao = isCancelada ? 'reativação' : 'renovação'
  const statusLabel = isAtiva && edicaoBookster ? `Assinatura ${edicaoBookster} ativa` : st.label
  const mostrarLinhaRenovacao = isCancelamentoEmCurso || ( Boolean( dataRenovacao ) && podeRenovar )

  return (
    <Root>
      <Head>
        <HeadBrand>
          <Title className="serif">
            { primeiroNome }, confira as informações da sua assinatura{ ' ' }
            <em>Bookster pelo Mundo</em>
          </Title>
          { mostrarLinhaRenovacao ? (
            <RenewLine>
              <IcoCalendar
                height="15"
                width="15"
              />
              { isCancelamentoEmCurso ? (
                <span>
                  Sua assinatura está <b>{ isCancelada ? 'cancelada' : 'em solicitação de cancelamento' }</b>
                  { ' ' }· a próxima renovação não vai acontecer.
                </span>
              ) : (
                <span>
                  Sua assinatura será renovada automaticamente em <b>{ renewFull }</b>.
                </span>
              ) }
            </RenewLine>
          ) : null }
        </HeadBrand>
        <StatusPill $status={ st.variant }>
          <StatusDot $status={ st.variant } />
          { statusLabel }
          { isCancelamentoEmCurso ? (
            <StatusPillCal
              aria-label="Ver detalhes do cancelamento"
              onClick={ onCancelClick }
              title="Ver detalhes do cancelamento"
              type="button"
            >
              <IcoCalendar
                height="14"
                width="14"
              />
            </StatusPillCal>
          ) : null }
        </StatusPill>
      </Head>

      { assinatura.ultimaVendaFalhou ? (
        <AlertaFalha>
          <AlertaFalhaIcon>
            <IcoStop
              height="20"
              width="20"
            />
          </AlertaFalhaIcon>
          <AlertaFalhaBody>
            <p>
              Não conseguimos concluir a última cobrança. <b>O pagamento foi recusado</b> (pode
              ser saldo, cartão expirado ou bloqueio do banco). Atualize sua forma de pagamento e
              tente novamente para manter sua assinatura ativa.
            </p>
            <AlertaFalhaBtn
              onClick={ onPaymentClick }
              type="button"
            >
              Atualizar forma de pagamento
            </AlertaFalhaBtn>
          </AlertaFalhaBody>
        </AlertaFalha>
      ) : null }

      <HeroStats>
        <HeroPrice>
          { valorPago ? (
            <>
              <StatKey>Valor da assinatura { edicaoBookster }</StatKey>
              <PriceValue>
                <PriceBig>{ formatBRL( valorPago.mensal ) }</PriceBig>
              </PriceValue>
              <PriceSmall>
                { formatBRL( valorPago.totalAnual ) } no total do ano
              </PriceSmall>
            </>
          ) : (
            <>
              <StatKey>Valor da { termoRenovacao }</StatKey>
              <PriceValue>
                { temDescontoAssociado ? <PriceOld>{ formatBRL( precoRenovacao.original ) }</PriceOld> : null }
                <PriceBig>{ precoRenovacao ? formatBRL( precoRenovacao.promo ) : '—' }</PriceBig>
              </PriceValue>
              { precoRenovacao ? (
                <PriceSmall>
                  12x de { formatBRL( precoRenovacao.promo ) } · { formatBRL( precoRenovacao.totalAnual ) } no total do ano
                </PriceSmall>
              ) : null }
              { precoRenovacao ? (
                <PriceNotes>
                  { temDescontoAssociado ? (
                    <PriceNote>Desconto exclusivo para associados</PriceNote>
                  ) : null }
                  <PriceNote>Frete grátis</PriceNote>
                </PriceNotes>
              ) : null }
            </>
          ) }
        </HeroPrice>
        <Brindes
          brindes={ brindes }
          isReativacao={ isCancelada }
        />
      </HeroStats>

      <Actions>
        <ActionsEyebrow>
          Gerenciar renovação de { nomeAssinatura }{ edicaoRenovacao ? ` ${edicaoRenovacao}` : '' }
        </ActionsEyebrow>
        <ActionsNota>
          Aqui você confere todas as informações referentes à sua assinatura atual
          { edicaoRenovacao ? ` e à renovação para ${edicaoRenovacao}` : ' e à renovação' }.
        </ActionsNota>
        <ActionsGrid>
          { isAtiva && podeRenovar ? (
            <PAction
              icon={ <IcoCheck /> }
              meta="Antecipar renovação"
              onClick={ onRenewClick }
              primary
              title="Renovar agora"
            />
          ) : null }
          { isAtiva && !podeRenovar ? (
            <PAction
              done
              icon={ <IcoCheck /> }
              meta="Renovada por mais um ano"
              title="Renovação ativa"
            />
          ) : null }
          { isCancelamentoAgendado ? (
            <PAction
              icon={ <IcoHeart /> }
              meta="Volte atrás e siga no clube"
              onClick={ onUndoClick }
              primary
              title="Desfazer cancelamento"
            />
          ) : null }
          { isCancelada ? (
            <PAction
              icon={ <IcoHeart /> }
              meta="Continue a viagem"
              onClick={ onReactivateClick }
              primary
              title="Reativar assinatura"
            />
          ) : null }
          { isCancelamentoAgendado ? null : (
            <>
              <PAction
                dica="Clique para editar a forma de pagamento"
                icon={ <IcoCard /> }
                meta={ cartaoMeta }
                onClick={ onPaymentClick }
                title="Formas de pagamento para renovação 2027"
              />
              <PAction
                icon={ <IcoPin /> }
                meta={ enderecoMeta }
                onClick={ onAddressClick }
                title="Endereço de entrega para renovação 2027"
              />
            </>
          ) }
          { isAtiva ? (
            <PAction
              danger
              icon={ <IcoStop /> }
              meta="Encerra a renovação"
              onClick={ onCancelClick }
              title="Cancelar renovação"
            />
          ) : null }
        </ActionsGrid>
      </Actions>
    </Root>
  )
}

Passport.propTypes = {
  'assinatura': object.isRequired,
  'associado': object,
  'nomeAssinatura': string,
  'cardPrincipal': shape( {
    'brand': string,
    'last4': string
  } ),
  'addrPrincipal': shape( {
    'cidade': string,
    'uf': string
  } ),
  'totalAddrs': number,
  'formaPagamento': string,
  'precoRenovacao': shape( {
    'original': number,
    'promo': number,
    'totalAnual': number,
    'totalAnualCheio': number
  } ),
  'onRenewClick': func,
  'onPaymentClick': func,
  'onAddressClick': func,
  'onCancelClick': func,
  'onReactivateClick': func,
  'onUndoClick': func
}

Passport.defaultProps = {
  'associado': null,
  'nomeAssinatura': 'assinatura',
  'cardPrincipal': null,
  'addrPrincipal': null,
  'totalAddrs': 0,
  'formaPagamento': 'card',
  'precoRenovacao': null,
  'onRenewClick': null,
  'onPaymentClick': null,
  'onAddressClick': null,
  'onCancelClick': null,
  'onReactivateClick': null,
  'onUndoClick': null
}
